import React, { useEffect, useState } from 'react';
import { SimplexNoise } from '@paper-design/shaders-react';

const AnimatedBackgroundReact = () => {
  const [isDark, setIsDark] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [webglSupported, setWebglSupported] = useState(null);

  // Configuration: Set to true to use rising glyphs instead of gradient fallback
  const USE_RISING_GLYPHS_FALLBACK = true;
  
  // Enhanced WebGL detection function with verbose logging and Chrome-specific checks
  const detectWebGL = () => {
    console.group('🔍 WebGL Detection Process');

    try {
      console.log('1️⃣ Creating test canvas...');
      const canvas = document.createElement('canvas');

      // Check browser type for specific handling
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
      console.log('🌐 Browser detected:', isChrome ? 'Chrome' : isSafari ? 'Safari' : 'Other');

      console.log('2️⃣ Attempting WebGL context creation...');
      // Try different context options for better Chrome compatibility
      const contextOptions = {
        alpha: true,
        antialias: false, // Disable antialiasing for better Chrome compatibility
        depth: false,
        failIfMajorPerformanceCaveat: false, // Allow software rendering
        powerPreference: 'default', // Don't force high-performance GPU
        premultipliedAlpha: true,
        preserveDrawingBuffer: false,
        stencil: false
      };

      let gl = canvas.getContext('webgl2', contextOptions) ||
               canvas.getContext('webgl', contextOptions) ||
               canvas.getContext('experimental-webgl', contextOptions);

      if (!gl) {
        console.error('❌ FAILED: WebGL context creation failed');
        console.log('   - Browser may not support WebGL');
        console.log('   - Hardware acceleration may be disabled');
        console.log('   - GPU may be blacklisted');
        console.log('   - In Chrome: check chrome://settings/system');
        console.groupEnd();
        console.warn('🔍 WebGL not supported - using CSS fallback background');
        return false;
      }
      console.log('✅ PASSED: WebGL context created successfully');
      console.log('   - WebGL version:', gl.getParameter(gl.VERSION));
      console.log('   - GLSL version:', gl.getParameter(gl.SHADING_LANGUAGE_VERSION));
      console.log('   - Renderer:', gl.getParameter(gl.RENDERER));

      console.log('3️⃣ Checking WebGL context state...');
      const isContextLost = gl.isContextLost();
      if (isContextLost) {
        console.error('❌ FAILED: WebGL context is lost');
        console.log('   - Context may have been lost due to GPU reset');
        console.log('   - Driver issues or system instability');
        console.groupEnd();
        console.warn('🔍 WebGL context lost - using CSS fallback background');
        return false;
      }
      console.log('✅ PASSED: WebGL context is active and healthy');

      console.log('4️⃣ Testing shader compilation...');
      const vertexShader = gl.createShader(gl.VERTEX_SHADER);
      const testShaderSource = 'attribute vec4 position; void main() { gl_Position = position; }';
      gl.shaderSource(vertexShader, testShaderSource);
      gl.compileShader(vertexShader);

      if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
        const shaderError = gl.getShaderInfoLog(vertexShader);
        console.error('❌ FAILED: Shader compilation failed');
        console.log('   - Shader error:', shaderError);
        console.log('   - GPU may not support required shader features');
        gl.deleteShader(vertexShader);
        console.groupEnd();
        console.warn('🔍 WebGL shader compilation failed - using CSS fallback background');
        return false;
      }
      console.log('✅ PASSED: Basic shader compilation successful');

      // Chrome-specific Paper Shaders compatibility check
      if (isChrome) {
        console.log('5️⃣ Chrome-specific Paper Shaders compatibility check...');
        try {
          // Test if the browser can handle complex shaders like those used by Paper Shaders
          const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
          const complexShaderSource = `
            precision mediump float;
            uniform float time;
            void main() {
              vec2 uv = gl_FragCoord.xy / vec2(256.0);
              float noise = sin(uv.x * 10.0 + time) * cos(uv.y * 10.0 + time);
              gl_FragColor = vec4(noise, noise, noise, 1.0);
            }`;
          gl.shaderSource(fragmentShader, complexShaderSource);
          gl.compileShader(fragmentShader);

          if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.warn('⚠️ WARNING: Chrome may have issues with complex shaders');
            console.log('   - This might affect Paper Shaders performance');
            console.log('   - Will attempt Paper Shaders but may fallback');
          } else {
            console.log('✅ PASSED: Chrome can handle complex shaders');
          }
          gl.deleteShader(fragmentShader);
        } catch (chromeError) {
          console.warn('⚠️ WARNING: Chrome shader test failed:', chromeError.message);
        }
      }

      console.log('6️⃣ Cleaning up test resources...');
      gl.deleteShader(vertexShader);
      console.log('✅ PASSED: Resources cleaned up');

      console.log('🎉 All WebGL checks passed!');
      console.groupEnd();
      console.log('✅ WebGL fully supported - using Paper Shaders background');
      return true;
    } catch (e) {
      console.error('❌ FAILED: WebGL detection threw an exception');
      console.log('   - Exception:', e.message);
      console.log('   - Stack:', e.stack);
      console.groupEnd();
      console.warn('🔍 WebGL detection error:', e.message, '- using CSS fallback background');
      return false;
    }
  };

  // Low-performance device detection with verbose logging
  const isLowPerformance = () => {
    console.group('📊 Device Performance Analysis');

    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const cores = navigator.hardwareConcurrency || 'unknown';
    const memory = navigator.deviceMemory || 'unknown';

    console.log('🔍 User Agent:', userAgent);
    console.log('📱 Mobile Device:', isMobile ? 'Yes' : 'No');
    console.log('🧠 CPU Cores:', cores);
    console.log('💾 Device Memory:', memory + 'GB');

    const isLowPerfDevice = isMobile || cores <= 2 || memory <= 4;

    if (isLowPerfDevice) {
      console.log('⚠️ Low-performance device detected');
      if (isMobile) console.log('   - Mobile device detected');
      if (cores <= 2) console.log('   - Limited CPU cores (' + cores + ' ≤ 2)');
      if (memory <= 4) console.log('   - Limited memory (' + memory + 'GB ≤ 4GB)');
    } else {
      console.log('🚀 High-performance device detected');
    }

    console.groupEnd();
    return isLowPerfDevice;
  };

  // GPU-optimized settings with verbose logging
  const getGpuSettings = () => {
    const isLowPerf = isLowPerformance();

    const settings = isLowPerf ? {
      stepsPerColor: 6,
      softness: 0,
      scale: 0.6,
      speed: 0.05,
    } : {
      stepsPerColor: 2,
      softness: 0,
      scale: 1.2,
      speed: 0.3,
    };

    console.group('⚙️ GPU Settings Configuration');
    console.log('📊 Performance Tier:', isLowPerf ? 'Low Performance' : 'High Performance');
    console.log('🎨 Animation Settings:');
    console.log('   - Steps Per Color:', settings.stepsPerColor, isLowPerf ? '(smoother)' : '(more visible)');
    console.log('   - Softness:', settings.softness);
    console.log('   - Scale:', settings.scale, isLowPerf ? '(reduced)' : '(enhanced)');
    console.log('   - Speed:', settings.speed, isLowPerf ? '(slower)' : '(faster)');
    console.groupEnd();

    return settings;
  };

  const gpuSettings = getGpuSettings();

  // Pause animation when battery is low
  const batteryOptimized = async () => {
    if ('getBattery' in navigator) {
      const battery = await navigator.getBattery();
      return battery.level < 0.2 || battery.charging === false;
    }
    return false;
  };

  // Monitor theme changes and WebGL support with verbose logging
  useEffect(() => {
    console.group('🎬 AnimatedBackground Component Initialization');
    console.log('1️⃣ Component mounting...');

    setIsMounted(true);

    console.log('2️⃣ Running WebGL detection...');
    const webglResult = detectWebGL();
    setWebglSupported(webglResult);

    const checkTheme = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      console.log('🌓 Theme detected:', isDarkMode ? 'Dark Mode' : 'Light Mode');
      setIsDark(isDarkMode);
    };

    console.log('3️⃣ Checking initial theme...');
    checkTheme();

    console.log('4️⃣ Setting up theme observer...');
    // Watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          console.log('🌓 Theme change detected, updating...');
          checkTheme();
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    console.log('✅ Component initialization complete');
    console.groupEnd();

    return () => {
      console.log('🧹 AnimatedBackground cleanup: disconnecting theme observer');
      observer.disconnect();
    };
  }, []);

  // Light mode colors with your OKLCH conversions (matching Next.js exactly)
  const lightColors = [
    "hsl(50, 85%, 75%)",   // oklch(82.8% 0.189 84.429) - warm golden
    "hsl(30, 70%, 45%)",   // oklch(55.5% 0.163 48.998) - warm orange-brown
    "hsl(340, 100%, 91%)", // soft pink
    "hsl(359, 94%, 62%)",  // vibrant red
    "hsl(42, 100%, 71%)",  // bright yellow
  ];
  
  // Dark mode colors (deeper, more saturated versions) (matching Next.js exactly)
  const darkColors = [
    "hsl(50, 70%, 35%)",   // darker golden
    "hsl(30, 85%, 25%)",   // deeper orange-brown
    "hsl(340, 80%, 45%)",  // deeper pink
    "hsl(359, 90%, 40%)",  // deeper red
    "hsl(42, 85%, 35%)",   // deeper yellow
  ];

  // CSS-only animated fallback component
  const CSSAnimatedBackground = () => {
    return React.createElement('div', {
      id: 'animated-background-css-fallback',
      className: 'fixed inset-0 -z-10 overflow-hidden pointer-events-none',
      style: {
        background: isDark
          ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.4) 0%, rgba(22, 33, 62, 0.3) 100%, rgba(15, 52, 96, 0.4) 100%, rgba(83, 52, 131, 0.3) 100%, rgba(26, 26, 46, 0.4) 100%)'
          : 'linear-gradient(135deg, rgba(255, 107, 53, 0.5) 0%, rgba(247, 147, 30, 0.4) 100%, rgba(255, 144, 104, 0.5) 100%, rgba(255, 167, 38, 0.4) 100%, rgba(255, 107, 53, 0.5) 100%)',
        backgroundSize: '400% 400%',
        animation: isDark ? 'gradientShift 20s ease infinite' : 'gradientShift 15s ease infinite'
      }
    });
  };

  // Rising glyphs animated background component (alternative)
  const RisingGlyphsBackground = () => {
    const glyphs = [
      "Potred", "底線", "identity", "界線", "honesty", "唔識收火", "🔥", "煲", "直話直說",
      "critical", "反思", "毒舌", "保底", "界限感", "真相", "🫙", "tar", "no sugarcoat",
      "red", "blue", "white", "orange", "💬", "戀愛", "關係", "煲過火", "燒底", "真話"
    ];

    const makeBlabber = (len = 6) => {
      const pick = () => glyphs[Math.floor(Math.random() * glyphs.length)];
      const SEP = Math.random() < 0.5 ? " · " : "  ";
      const out = Array.from({length: len}, pick);
      return out.join(SEP);
    };

    // Create 20 glyph columns
    const glyphColumns = Array.from({length: 20}, (_, i) => {
      const delay = Math.random() * 4;
      const duration = 9 + Math.random() * 4;
      const left = (i + 1) * 5; // 5%, 10%, 15%, etc.

      return React.createElement('div', {
        key: i,
        className: 'potred-glyph falling',
        style: {
          position: 'absolute',
          left: `${left}%`,
          top: '-12vh',
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`
        }
      }, React.createElement('span', {
        className: 'potred-glyph-inner',
        style: {
          fontSize: `${18 + Math.random() * 16}px`,
          color: isDark
            ? ['rgba(59, 130, 246, 0.4)', 'rgba(139, 92, 246, 0.4)', 'rgba(239, 68, 68, 0.4)', 'rgba(255, 255, 255, 0.3)'][i % 4]
            : ['rgba(234, 88, 12, 0.5)', 'rgba(249, 115, 22, 0.5)', 'rgba(239, 68, 68, 0.5)', 'rgba(31, 41, 55, 0.4)'][i % 4],
          animationDelay: `${Math.random() * 2}s`
        }
      }, makeBlabber(3 + Math.floor(Math.random() * 4))));
    });

    return React.createElement('div', {
      id: 'animated-background-rising-glyphs',
      className: 'potred-glyphs-wrapper',
      style: {
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(180deg, rgb(35, 38, 52) 10%, rgb(76, 79, 105) 50%, rgb(114, 135, 253) 80%, rgb(76, 79, 105) 100%)'
          : 'linear-gradient(180deg, rgba(254, 247, 237) 0%, rgb(239, 159, 118) 70%, rgba(253, 186, 116) 100%)',
        pointerEvents: 'none'
      }
    }, [
      // Backdrop with rotating pattern
      React.createElement('div', {
        key: 'backdrop',
        className: 'potred-backdrop',
        style: {
          position: 'absolute',
          inset: '-12vmax',
          backgroundImage: `conic-gradient(
            from 0deg at 50% 50%,
            transparent 0deg,
            ${isDark ? 'rgba(255,255,255,.025)' : 'rgba(0,0,0,.025)'} 36deg,
            transparent 72deg,
            ${isDark ? 'rgba(255,255,255,.02)' : 'rgba(0,0,0,.02)'} 144deg,
            transparent 216deg,
            ${isDark ? 'rgba(255,255,255,.03)' : 'rgba(0,0,0,.03)'} 288deg,
            transparent 360deg
          )`,
          filter: 'blur(20px)',
          opacity: 0.08,
          animation: 'potredRotatePattern 60s linear infinite',
          zIndex: 0
        }
      }),
      // Glyph columns
      React.createElement('div', {
        key: 'glyphs',
        style: {
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1
        }
      }, glyphColumns)
    ]);
  };

  // Loading state - show fallback
  if (!isMounted || webglSupported === null) {
    console.log('⏳ Rendering loading state - component not ready yet');
    return React.createElement('div', {
      id: 'animated-background-loading',
      className: 'fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-gradient-to-br from-orange-50 via-pink-50 to-yellow-50 dark:from-orange-950 dark:via-rose-950 dark:to-yellow-950'
    });
  }

  // WebGL not supported - use CSS animated fallback (configurable)
  if (!webglSupported) {
    console.group('🎭 WebGL Not Supported - Using Fallback');
    console.log('❌ WebGL failed detection criteria (see above)');
    console.log('🔄 Fallback mode:', USE_RISING_GLYPHS_FALLBACK ? 'Rising Glyphs Animation' : 'CSS Gradient Animation');
    console.groupEnd();
    return USE_RISING_GLYPHS_FALLBACK ? RisingGlyphsBackground() : CSSAnimatedBackground();
  }

  // WebGL supported - try Paper Shaders with enhanced error boundary and Chrome fallback
  console.group('🎨 Rendering WebGL Animation');
  console.log('✅ WebGL supported - attempting Paper Shaders SimplexNoise');
  console.log('🎨 Using colors:', isDark ? 'Dark Mode Palette' : 'Light Mode Palette');
  console.log('⚙️ GPU Settings Applied:', {
    stepsPerColor: gpuSettings.stepsPerColor,
    softness: gpuSettings.softness,
    scale: gpuSettings.scale,
    speed: gpuSettings.speed
  });

  // Detect browser for specific handling
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

  try {
    // For Chrome, use more conservative settings to avoid crashes
    const chromeOptimizedSettings = isChrome ? {
      stepsPerColor: Math.max(4, gpuSettings.stepsPerColor), // Minimum 4 for Chrome stability
      softness: gpuSettings.softness,
      scale: Math.min(1.0, gpuSettings.scale), // Cap scale for Chrome
      speed: Math.min(0.2, gpuSettings.speed) // Reduce speed for Chrome stability
    } : gpuSettings;

    if (isChrome) {
      console.log('🌐 Chrome detected - using optimized settings:', chromeOptimizedSettings);
    }

    const result = React.createElement('div', {
      id: 'animated-background',
      className: 'fixed inset-0 -z-10 overflow-hidden pointer-events-none'
    },
      React.createElement(SimplexNoise, {
        style: {
          height: '100vh',
          width: '100vw',
          position: 'absolute',
          top: 0,
          left: 0
        },
        stepsPerColor: chromeOptimizedSettings.stepsPerColor,
        softness: chromeOptimizedSettings.softness,
        scale: chromeOptimizedSettings.scale,
        rotation: 0,
        speed: chromeOptimizedSettings.speed,
        colors: isDark ? darkColors : lightColors,
        // Add key prop to force re-render if needed
        key: `simplex-${isChrome ? 'chrome' : 'other'}-${isDark ? 'dark' : 'light'}`
      })
    );

    console.log('✅ SimplexNoise component created successfully');
    console.groupEnd();
    return result;
  } catch (error) {
    console.error('❌ Paper Shaders failed to render:', error.message);
    console.log('   - Error stack:', error.stack);
    if (isChrome) {
      console.log('🌐 Chrome-specific error detected');
      console.log('   - This is a known issue with Paper Shaders in Chrome');
      console.log('   - Falling back to alternative animation');
    }
    console.log('🔄 Falling back to:', USE_RISING_GLYPHS_FALLBACK ? 'Rising Glyphs' : 'CSS Animation');
    console.groupEnd();
    console.warn('🔍 Paper Shaders failed to render:', error.message, '- falling back to CSS animation');
    return USE_RISING_GLYPHS_FALLBACK ? RisingGlyphsBackground() : CSSAnimatedBackground();
  }
};

export default AnimatedBackgroundReact;